<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const totalItems = computed(() => props.data?.total_items || 0);
const openDeals = computed(() => props.data?.total_open || 0);
const wonDeals = computed(() => props.data?.total_won || 0);
const lostDeals = computed(() => props.data?.total_lost || 0);

const openPercentage = computed(() => {
  if (totalItems.value === 0) return 0;
  return ((openDeals.value / totalItems.value) * 100).toFixed(1);
});

const wonPercentage = computed(() => {
  if (totalItems.value === 0) return 0;
  return ((wonDeals.value / totalItems.value) * 100).toFixed(1);
});

const lostPercentage = computed(() => {
  if (totalItems.value === 0) return 0;
  return ((lostDeals.value / totalItems.value) * 100).toFixed(1);
});
</script>

<template>
  <div class="rounded-lg border border-n-weak bg-n-solid-2 p-5">
    <!-- Header -->
    <div class="mb-4 flex items-center justify-between">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ $t('PIPELINE.ANALYTICS.PIPELINE.TITLE') }}
      </h3>
      <Icon icon="i-lucide-bar-chart-3" class="size-5 text-n-slate-9" />
    </div>

    <!-- Total Items -->
    <div class="mb-5">
      <div class="text-xs text-n-slate-11">
        {{ $t('PIPELINE.ANALYTICS.PIPELINE.TOTAL_ITEMS') }}
      </div>
      <div class="text-3xl font-semibold text-n-slate-12">
        {{ totalItems }}
      </div>
    </div>

    <!-- Pipeline Breakdown -->
    <div class="space-y-3">
      <!-- Open Deals -->
      <div class="rounded-lg border border-n-blue-6 bg-n-blue-3 p-3">
        <div class="mb-2 flex items-center justify-between">
          <span class="text-xs font-medium text-n-blue-11">
            {{ $t('PIPELINE.ANALYTICS.PIPELINE.OPEN_DEALS') }}
          </span>
          <span class="text-base font-semibold text-n-blue-11">
            {{ openDeals }}
          </span>
        </div>
        <div class="h-1.5 overflow-hidden rounded-full bg-n-blue-6">
          <div
            class="h-full bg-n-blue-9 transition-all duration-500"
            :style="{ width: `${openPercentage}%` }"
          />
        </div>
        <div class="mt-1 text-right text-[10px] text-n-blue-11">
          {{ openPercentage }}%
        </div>
      </div>

      <!-- Won Deals -->
      <div class="rounded-lg border border-n-teal-6 bg-n-teal-3 p-3">
        <div class="mb-2 flex items-center justify-between">
          <span class="text-xs font-medium text-n-teal-11">
            {{ $t('PIPELINE.ANALYTICS.PIPELINE.WON_DEALS') }}
          </span>
          <span class="text-base font-semibold text-n-teal-11">
            {{ wonDeals }}
          </span>
        </div>
        <div class="h-1.5 overflow-hidden rounded-full bg-n-teal-6">
          <div
            class="h-full bg-n-teal-9 transition-all duration-500"
            :style="{ width: `${wonPercentage}%` }"
          />
        </div>
        <div class="mt-1 text-right text-[10px] text-n-teal-11">
          {{ wonPercentage }}%
        </div>
      </div>

      <!-- Lost Deals -->
      <div class="rounded-lg border border-n-ruby-6 bg-n-ruby-3 p-3">
        <div class="mb-2 flex items-center justify-between">
          <span class="text-xs font-medium text-n-ruby-11">
            {{ $t('PIPELINE.ANALYTICS.PIPELINE.LOST_DEALS') }}
          </span>
          <span class="text-base font-semibold text-n-ruby-11">
            {{ lostDeals }}
          </span>
        </div>
        <div class="h-1.5 overflow-hidden rounded-full bg-n-ruby-6">
          <div
            class="h-full bg-n-ruby-9 transition-all duration-500"
            :style="{ width: `${lostPercentage}%` }"
          />
        </div>
        <div class="mt-1 text-right text-[10px] text-n-ruby-11">
          {{ lostPercentage }}%
        </div>
      </div>
    </div>
  </div>
</template>
